import type { TFunction } from "i18next";

import createDockPageRegistry, { type DockDetailRoute, type DockPageMeta } from "~/components/dock/dockPageRegistry";
import { ORDERS_DOCK_PAGE_ID, PRODUCTS_CATEGORIES_DOCK_PAGE_ID, PRODUCTS_ITEMS_DOCK_PAGE_ID } from "~/helpers/panelKey";

const createFulfillPages = (t: TFunction<"common">): DockPageMeta[] => [
	{
		hideRibbon: true,
		id: "dashboard",
		path: "/fulfill/dashboard",
		title: t("dock.pages.fulfill.dashboard")
	},
	{
		hideRibbon: true,
		id: "sales-performance",
		path: "/fulfill/sales-performance",
		title: t("dock.pages.fulfill.salesPerformance")
	},
	{
		hideRibbon: true,
		id: "cash-collections",
		path: "/fulfill/cash-collections",
		title: t("dock.pages.fulfill.cashCollections")
	},
	{
		id: ORDERS_DOCK_PAGE_ID,
		path: "/fulfill/orders",
		routePattern: "orders/*",
		title: t("dock.pages.fulfill.orders")
	},
	{
		id: "customers",
		path: "/fulfill/customers",
		routePattern: "customers/*",
		title: t("dock.pages.fulfill.customers")
	},
	{
		id: "customers-routes",
		path: "/fulfill/customers/routes",
		routePattern: "customers/*",
		title: t("dock.pages.fulfill.customersRoutes")
	},
	{
		id: "customers-groups",
		path: "/fulfill/customers/groups",
		routePattern: "customers/*",
		title: t("dock.pages.fulfill.customersGroups")
	},
	{
		id: PRODUCTS_ITEMS_DOCK_PAGE_ID,
		path: "/fulfill/product/items",
		routePattern: "product/*",
		title: t("dock.pages.fulfill.productsItems")
	},
	{
		id: PRODUCTS_CATEGORIES_DOCK_PAGE_ID,
		path: "/fulfill/product/categories",
		routePattern: "product/*",
		title: t("dock.pages.fulfill.productsCategories")
	},
	{
		id: "products-hard-goods",
		path: "/fulfill/product/hard-goods",
		routePattern: "product/*",
		title: t("dock.pages.fulfill.productsHardGoods")
	},
	{
		id: "products-kits",
		path: "/fulfill/product/kits",
		routePattern: "product/*",
		title: t("dock.pages.fulfill.productsKits")
	},
	{
		id: "products-containers",
		path: "/fulfill/product/maintenance/containers",
		routePattern: "product/*",
		title: t("dock.pages.fulfill.productsContainers")
	},
	{
		id: "products-shipping-config",
		path: "/fulfill/product/maintenance/categories",
		routePattern: "product/*",
		title: t("dock.pages.fulfill.productsShippingConfig")
	},
	{
		id: "sales-pricing",
		path: "/fulfill/sales/pricing",
		routePattern: "sales/*",
		title: t("dock.pages.fulfill.salesPricing")
	},
	{
		id: "sales-terms",
		path: "/fulfill/sales/terms",
		routePattern: "sales/*",
		title: t("dock.pages.fulfill.salesTerms")
	},
	{
		id: "sales-statements",
		path: "/fulfill/sales/statements",
		routePattern: "sales/*",
		title: t("dock.pages.fulfill.salesStatements")
	},
	{
		id: "sales-invoices",
		path: "/fulfill/sales/invoices",
		routePattern: "sales/*",
		title: t("dock.pages.fulfill.salesInvoices")
	},
	{
		id: "sales-receivables",
		path: "/fulfill/sales/receivables-aging",
		routePattern: "sales/*",
		title: t("dock.pages.fulfill.salesReceivables")
	},
	{
		id: "sales-receive-payments",
		path: "/fulfill/sales/receivables",
		routePattern: "sales/*",
		title: t("dock.pages.fulfill.salesReceivePayments")
	},
	{
		id: "purchasing-vendors",
		path: "/fulfill/purchasing/vendors",
		routePattern: "purchasing/*",
		title: t("dock.pages.fulfill.purchasingVendors")
	},
	{
		id: "purchasing-orders",
		path: "/fulfill/purchasing/orders",
		routePattern: "purchasing/*",
		title: t("dock.pages.fulfill.purchasingOrders")
	},
	{
		id: "locations",
		path: "/fulfill/locations",
		routePattern: "locations/*",
		title: t("dock.pages.fulfill.locations")
	},
	{
		id: "inventory-stock",
		path: "/fulfill/inventory",
		routePattern: "inventory/*",
		title: t("dock.pages.fulfill.inventoryStock")
	},
	{
		id: "logistics-planning",
		path: "/fulfill/logistics/planning",
		routePattern: "logistics/*",
		title: t("dock.pages.fulfill.logisticsPlanning")
	},
	{
		id: "logistics-deliveries",
		path: "/fulfill/logistics/deliveries",
		routePattern: "logistics/*",
		title: t("dock.pages.fulfill.logisticsDeliveries")
	},
	{
		id: "logistics-routing",
		path: "/fulfill/logistics/routing",
		routePattern: "logistics/*",
		title: t("dock.pages.fulfill.logisticsRouting")
	},
	{
		id: "logistics-carriers",
		path: "/fulfill/logistics/carriers",
		routePattern: "logistics/*",
		title: t("dock.pages.fulfill.logisticsCarriers")
	},
	{
		id: "logistics-fleet",
		path: "/fulfill/logistics/fleet",
		routePattern: "logistics/*",
		title: t("dock.pages.fulfill.logisticsFleet")
	},
	{
		id: "logistics-docks",
		path: "/fulfill/logistics/docks",
		routePattern: "logistics/*",
		title: t("dock.pages.fulfill.logisticsDocks")
	},
	{
		id: "portal-quick-orders",
		path: "/fulfill/portal/quick-orders",
		routePattern: "portal/*",
		title: t("dock.pages.fulfill.portalQuickOrders")
	},
	{
		id: "portal-branding",
		path: "/fulfill/portal/branding",
		routePattern: "portal/*",
		title: t("dock.pages.fulfill.portalBranding")
	},
	{
		id: "portal-settings",
		path: "/fulfill/portal/settings",
		routePattern: "portal/*",
		title: t("dock.pages.fulfill.portalSettings")
	},
	{
		id: "settings-general",
		path: "/fulfill/settings/general",
		routePattern: "settings/*",
		title: t("dock.pages.fulfill.settingsGeneral")
	},
	{
		id: "settings-sales-tax",
		path: "/fulfill/settings/sales-tax",
		routePattern: "settings/*",
		title: t("dock.pages.fulfill.settingsSalesTax")
	},
	{
		id: "settings-characteristics",
		path: "/fulfill/settings/characteristics",
		routePattern: "settings/*",
		title: t("dock.pages.fulfill.settingsCharacteristics")
	},
	{
		id: "settings-commissions",
		path: "/fulfill/settings/commissions",
		routePattern: "settings/*",
		title: t("dock.pages.fulfill.settingsCommissions")
	},
	{
		id: "report-viewer",
		path: "/fulfill/reports",
		routePattern: "reports/:reportId?",
		title: t("dock.pages.reports.section")
	}
];

const createFulfillDetailRoutes = (t: TFunction<"common">): DockDetailRoute[] => [
	{
		componentId: "orders",
		match: (pathname) => {
			const match = pathname.match(/^\/fulfill\/orders\/(order|master-order)\/([^/]+)/);
			if (!match) {
				return null;
			}
			const [, kind, entityId] = match;
			return {
				id: `${kind}-${entityId}`,
				path: `/fulfill/orders/${kind}/${entityId}`,
				title: kind === "master-order" ? t("dock.pages.fulfill.masterOrder") : t("dock.pages.fulfill.order")
			};
		}
	},
	{
		componentId: "customers",
		match: (pathname) => {
			const match = pathname.match(/^\/fulfill\/customers\/customer\/([^/]+)/);
			if (!match) {
				return null;
			}
			const [, customerId] = match;
			return {
				id: `customer-${customerId}`,
				path: `/fulfill/customers/customer/${customerId}`,
				title: t("dock.pages.fulfill.customer")
			};
		}
	},
	{
		componentId: "purchasing-orders",
		match: (pathname) => {
			const match = pathname.match(/^\/fulfill\/purchasing\/orders\/([^/]+)/);
			if (!match) return null;
			const [, purchaseOrderId] = match;
			return {
				id: `purchase-order-${purchaseOrderId}`,
				path: `/fulfill/purchasing/orders/${purchaseOrderId}`,
				title: t("dock.pages.fulfill.purchaseOrder")
			};
		}
	},
	{
		componentId: "report-viewer",
		match: (pathname) => {
			const match = pathname.match(/^\/fulfill\/reports\/([^/]+)/);
			if (!match) {
				return null;
			}
			const [, reportId] = match;
			return {
				id: `report-${reportId}`,
				path: `/fulfill/reports/${reportId}`,
				title: t("dock.pages.reports.section")
			};
		}
	}
];

const createFulfillDockPageRegistry = (
	t: TFunction<"common">,
	dashboardEnabled: boolean,
	kitsEnabled: boolean,
	logisticsEnabled: boolean
) =>
	createDockPageRegistry(
		createFulfillPages(t).filter(
			(page) =>
				(dashboardEnabled ||
					(page.id !== "dashboard" && page.id !== "sales-performance" && page.id !== "cash-collections")) &&
				(kitsEnabled || page.id !== "products-kits") &&
				(logisticsEnabled || !page.id.startsWith("logistics-"))
		),
		dashboardEnabled ? "dashboard" : ORDERS_DOCK_PAGE_ID,
		createFulfillDetailRoutes(t)
	);

export default createFulfillDockPageRegistry;
